import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import { commitGpsFix } from "../utils/address";
import { GPS_FLOW_ENABLED, hasKnownGpsDenial, requestLocation } from "../utils/resolveLocation";
import waitForLogin from "../utils/waitForLogin";
import { deriveUserCoords } from "./useUserLocation";

// One attempt per page load, not per mount: navigating back to home within a
// session must not re-raise the dialog, but every fresh webview open (a new JS
// context) asks again until the user actually answers.
let attemptedThisLoad = false;

// Same cap as welcome's CTA: rung 2 of the coordinate ladder (the profile
// address) arrives with the Login response, so a no-coords decision must not
// be made before it lands — but never waits on it forever.
const LOGIN_WAIT_CAP_MS = 8000;

// Location on home entry. Welcome used to own this (it re-resolved GPS on every
// entry), but welcome now runs only on the very first onboarding — so home both
// refreshes a granted fix and, when the permission is still undecided, raises the
// permission dialog itself. Permission state is the only trigger: it runs even when
// the profile address already supplies coordinates (an allowed fix wins
// last-write-wins; a denial leaves the address ladder untouched).
//
// The rule is "attempt unless a denial is known": "granted" refreshes silently,
// an undecided state attempts — which is what raises the permission dialog
// (the browser's own, or the host app's OS dialog inside a WebView) — and a
// *known* denial never attempts and never redirects (no nag loop). "Known"
// is hasKnownGpsDenial's evidence-based answer: a live "denied" reading, or —
// in the WebView, where the permission API keeps reading "prompt" even for a
// denied user — a denial a previous real attempt came back with. That memory is
// what keeps the Clalit app's denied-request response (opening the device
// settings) from ever firing off home twice: the first answered attempt records
// the denial, and every later entry is a silent, locationless home until a
// grant is observed (a "granted" reading, or any successful fix) clears it.
//
// When the attempt this hook itself raised comes back with nothing (denied,
// unavailable, or the 60s watchdog) AND the whole coordinate ladder is empty —
// no saved clientAddress, no profile address once Login has landed — the user
// has no location at all, and home routes them to the /location-required gate,
// the same decision welcome's CTA makes. Only a *fresh* failure does this: a
// permission already "denied" at entry returns before the attempt, so a user
// who refused in a past visit (or escaped the gate via its X) keeps a working,
// locationless home instead of being nagged on every load.
//
// Never fires during a new user's onboarding: it waits for the handshake Token,
// which silentLogin never dispatches before the IsOtpRequired early-return — so
// Welcome's CTA stays the first-ever prompt, and by the time home mounts for that
// user the permission is already decided. commitGpsFix's 100m same-place guard
// keeps a user who hasn't moved from churning the backend or the carousels.
//
// Returns whether the attempt has settled — i.e. this entry's GPS can no longer
// change the location (denied, failed, or the fix is already dispatched). The
// carousel gate holds its first round on this, so the entry fetches once, with
// the final coordinates, instead of once per rung of the location ladder.
export default function useEntryGpsRefresh() {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  // commitGpsFix no-ops without credentials, so wait for the handshake's Token
  // rather than firing into a void on first paint.
  const token = useSelector((state) => state.userSlice.mainUserData?.Token);
  // A repeat mount in the same page load reports settled immediately: the attempt
  // already ran (or its continuation will land in the store on its own).
  const [gpsSettled, setGpsSettled] = useState(() => attemptedThisLoad);
  // The gate redirect below sits behind awaits (the prompt, the Login wait); if
  // the user tapped elsewhere — or silentLogin routed them — meanwhile, home is
  // gone and its decision is stale. Only the mount that started the attempt, and
  // only while it is still on screen, may navigate.
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // GPS flow disabled: no attempt, no /location-required — but the settle must
    // still happen, or the carousel gate would hold its first fetch for nothing.
    if (!GPS_FLOW_ENABLED) {
      setGpsSettled(true);
      return;
    }
    if (attemptedThisLoad || !token) return;
    attemptedThisLoad = true;
    (async () => {
      try {
        // See the header: no attempt, no gate, no settings jump once a denial
        // is known — otherwise attempt, letting the dialog appear if undecided.
        if (await hasKnownGpsDenial()) return;

        const result = await requestLocation();
        if (result.coords) {
          // Live values, not render-time props: Login may have landed during the fix.
          // Not awaited — the coordinates are dispatched synchronously before its
          // background reverse-geocode, so they're in the store before the settle
          // below releases the carousel gate.
          const { clientAddress, mainUserData } = store.getState().userSlice;
          commitGpsFix({ coords: result.coords, clientAddress, mainUserData, dispatch });
          return;
        }

        // The attempt ran and gave nothing — a fresh, user-answered outcome
        // (an explicit denial settles instantly; only the ignored-prompt /
        // no-fix cases ride out the watchdog first), so the gate below is only
        // ever reached off a real attempt. The ladder check waits for Login,
        // exactly like welcome, so a user with a profile address on file is never
        // bounced to the gate by a race with that response.
        await waitForLogin(store, LOGIN_WAIT_CAP_MS);
        const { coords: knownCoords } = deriveUserCoords(store.getState().userSlice);
        if (!knownCoords && mountedRef.current) {
          // `replace`: the gate must not sit in history on top of a locationless
          // home. Navigating before the settle in `finally` unmounts home first,
          // so the carousel gate never releases a wasted null-coords fetch.
          navigate("/location-required", { replace: true });
        }
      } catch {
        // A background refresh must never surface an error — home works without it.
      } finally {
        setGpsSettled(true);
      }
    })();
  }, [token, dispatch, store, navigate]);

  return gpsSettled;
}
